import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import {
  School,
  Dashboard,
  Quiz,
  Assessment,
  AccountCircle,
  Logout,
  Menu as MenuIcon
} from '@mui/icons-material';
import { useAuth } from '../App';
import logo from '../assets/ehsancadetlogo.png';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileAnchorEl, setMobileAnchorEl] = React.useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleClose();
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const NavButton = ({ path, label, icon, onClick }) => (
    <Button
      color="inherit"
      startIcon={icon}
      onClick={onClick || (() => navigate(path))}
      sx={{
        mx: 1,
        backgroundColor: isActive(path) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
      }}
    >
      {label}
    </Button>
  );

  const mobileItems = () => {
    const items = [];
    // Common
    items.push({ label: 'Dashboard', path: '/dashboard', icon: <Dashboard /> });
    if (user?.role === 'admin') {
      items.push({ label: 'Manage MCQs', path: '/admin/mcqs', icon: <Quiz /> });
      items.push({ label: 'Manage Tests', path: '/admin/tests', icon: <Assessment /> });
      items.push({ label: 'Students', path: '/admin/students', icon: <Dashboard /> });
      items.push({ label: 'View Results', path: '/results', icon: <Assessment /> });
    } else {
      items.push({ label: 'Take Test', path: '/test', icon: <Assessment /> });
      items.push({ label: 'My Results', path: '/results', icon: <Assessment /> });
    }
    return items;
  };

  const openMobileMenu = (e) => setMobileAnchorEl(e.currentTarget);
  const closeMobileMenu = () => setMobileAnchorEl(null);

  return (
    <AppBar position="static" elevation={2}>
      <Toolbar>
        <img src={logo} alt="Ehsan Cadet Academy" style={{ width: '60px', height: '60px' }} />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Ehsan Cadet Academy
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Desktop nav */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <NavButton path="/dashboard" label="Dashboard" icon={<Dashboard />} />
            {user?.role === 'admin' && (
              <>
                <NavButton path="/admin/mcqs" label="Manage MCQs" icon={<Quiz />} />
                <NavButton path="/admin/tests" label="Manage Tests" icon={<Assessment />} />
                <NavButton path="/admin/students" label="Students" icon={<Dashboard />} />
              </>
            )}
            {user?.role === 'student' && (
              <>
                <NavButton path="/test" label="Take Test" icon={<Assessment />} />
                <NavButton path="/results" label="My Results" icon={<Assessment />} />
              </>
            )}
          </Box>

          {/* Mobile nav trigger */}
          <Box sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 1 }}>
            <IconButton color="inherit" onClick={openMobileMenu} aria-label="open navigation menu">
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileAnchorEl}
              open={Boolean(mobileAnchorEl)}
              onClose={closeMobileMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {mobileItems().map((item) => (
                <MenuItem
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    closeMobileMenu();
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.icon}
                    <Typography>{item.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* User Menu */}
          <Box sx={{ ml: 1 }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <Box>
                  <Typography variant="subtitle2">{user?.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.email} ({user?.role})
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 